import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { WebcamProps } from 'react-webcam';
import Webcam from 'react-webcam';
import { Button } from '@components/atoms/button';
import { CameraAccessDefault } from '@components/atoms/camera-access-default';
import { CornerLine } from '@components/atoms/corner-line-window';
import { Icon } from '@components/atoms/icon';
import { Spinner } from '@components/atoms/spinner';
import { Typography } from '@components/atoms/typography';
import { ArrowTailIcon } from '@components/icons/arrow-tail-icon';
import { CameraVariantLineEnum, VerificationStepEnum } from '@models/enums';
import type { FileWithPreview, ThemeProps } from '@models/types';
import type { TakeDocumentMobileProps } from '@modules/dashboard/pages/docs-verification/docs-verification-page/downloads/take-document-mobile/types';
import useMediaQuery from '@mui/material/useMediaQuery';
import useTheme from '@mui/styles/useTheme';
import { base64ToFile, getEnvironmentVideoConstraints, isAndroid } from '@utils/util';
import cn from 'classnames';
import { isIOS } from 'mobile-device-detect';
import { v4 as uuidv4 } from 'uuid';

import { ANDROID_CAMERA_MOBILE_CONSTRAINTS, IOS_CAMERA_MOBILE_CONSTRAINTS } from '../constants';

import { Container, Content, Header } from './styles';

const cameraResolution = {
    width: {
        ideal: 4096,
        min: 720,
    },
    height: {
        ideal: 2160,
        min: 480,
    },
};

const faceConstraints = {
    audio: false,
    video: {
        facingMode: {
            exact: 'user',
            ...cameraResolution,
        },
    },
} as WebcamProps['videoConstraints'];

const android = isAndroid();

export const TakeDocumentMobile: React.FC<TakeDocumentMobileProps> = ({
    title,
    onClose,
    onChangeFile,
    variant,
    isUseNativeCamera,
}) => {
    const { t } = useTranslation();
    const theme = useTheme<ThemeProps>();
    const matchesMobileSm = useMediaQuery(`(max-width:${theme.breakPoints.sm})`);
    const webcamRef = useRef<Webcam>(null);
    const [previewFile, setPreviewFile] = useState<FileWithPreview | null>(null);
    const [currentStep, setCurrentStep] = useState<VerificationStepEnum>(
        VerificationStepEnum.CAMERA_ACCESS,
    );

    const [cameraNotAllowed, setIsCameraNotAllowed] = useState(false);

    const [videoConstraints, setVideoConstraints] = useState<MediaStreamConstraints | null>(null);
    const [webcamVideoConstrains, setWebcamVideoConstraints] = useState<
        WebcamProps['videoConstraints'] | null
    >(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const isInitialized = useRef(false);

    const handleOpenNativeCamera = useCallback(
        () =>
            new Promise((resolve) => {
                const input = document.createElement('input');

                input.type = 'file';
                input.accept = 'image/*';
                input.capture = isIOS ? 'environment' : 'user';

                const cancelDetector = () => {
                    window.removeEventListener('focus', cancelDetector);
                    if (input.files?.length === 0) {
                        if (onClose) {
                            onClose();
                        }
                    }
                };

                input.addEventListener('click', () => {
                    window.addEventListener('focus', cancelDetector, true);
                });

                input.addEventListener('change', () => {
                    // @ts-expect-error test
                    const file = input?.files[0];

                    Object.assign(file, {
                        preview: URL.createObjectURL(file),
                    });
                    resolve(file);
                    onChangeFile(file as FileWithPreview);
                    if (onClose) {
                        onClose(true);
                    }
                });

                input.click();
            }),
        [onClose, onChangeFile],
    );

    useEffect(() => {
        if (isUseNativeCamera) {
            if (!isInitialized.current && inputRef.current) {
                isInitialized.current = true;
                handleOpenNativeCamera();
            }

            return;
        }
        if (variant === CameraVariantLineEnum.SELFIE) {
            setVideoConstraints(faceConstraints as MediaStreamConstraints);
            setWebcamVideoConstraints({
                facingMode: 'user',
            });
        } else {
            getEnvironmentVideoConstraints().then((constraints) => {
                setVideoConstraints(constraints);
                setWebcamVideoConstraints({
                    facingMode:
                        (constraints.video.facingMode as { ideal: string })?.ideal ||
                        constraints.video.facingMode,
                    ...(android
                        ? ANDROID_CAMERA_MOBILE_CONSTRAINTS
                        : IOS_CAMERA_MOBILE_CONSTRAINTS),
                });
            });
        }
    }, [variant, isUseNativeCamera, handleOpenNativeCamera]);

    useEffect(() => {
        if (isUseNativeCamera) {
            return;
        }
        const getUserMedia = () => {
            navigator.mediaDevices
                .getUserMedia(videoConstraints as MediaStreamConstraints)
                .then((stream) => {
                    const tracks = stream.getTracks();

                    tracks?.forEach((track) => {
                        track.stop();
                    });
                    setCurrentStep(VerificationStepEnum.SCANNING);
                })
                .catch(() => {
                    setIsCameraNotAllowed(true);
                });
        };

        if (videoConstraints) {
            getUserMedia();
        }
    }, [variant, videoConstraints, isUseNativeCamera]);

    const capture = useCallback(async () => {
        if (webcamRef.current) {
            // const imageSrc = webcamRef.current.getScreenshot();
            let imageSrc;

            if (matchesMobileSm && !android) {
                const multiplier = 2;

                imageSrc = webcamRef.current.getScreenshot({
                    width: window.innerWidth * multiplier,
                    height: window.innerHeight * multiplier,
                });
            } else {
                const multiplier = 2;

                imageSrc = webcamRef.current.getScreenshot({
                    width: window.innerWidth * multiplier,
                    height: window.innerHeight * multiplier,
                });
            }
            const file = (await base64ToFile(
                imageSrc as string,
                `photo_${uuidv4()}.jpeg`,
            )) as FileWithPreview;

            const doneFile = Object.assign(file, {
                preview: imageSrc,
            });

            setPreviewFile(doneFile);
        }
    }, [matchesMobileSm]);

    const handleClose = () => {
        onClose?.();
    };

    const handleFileChange = () => {
        if (previewFile) {
            onChangeFile(previewFile);
        }
        if (onClose) {
            onClose(true);
        }
    };

    const handleRedoClick = () => {
        setPreviewFile(null);
    };

    return (
        <Container
            className={cn({
                'take-address-container': variant === CameraVariantLineEnum.POA,
            })}
        >
            {/* if remove this input then handleOpenNativeCamera not work  */}
            {isUseNativeCamera && (
                <React.Fragment>
                    <input
                        ref={inputRef}
                        type='file'
                        name='file'
                        accept='capture=camera'
                        capture={true}
                        className='hidden-input'
                    />
                </React.Fragment>
            )}
            {cameraNotAllowed ? (
                <CameraAccessDefault onClose={onClose} />
            ) : (
                <React.Fragment>
                    {currentStep === VerificationStepEnum.CAMERA_ACCESS && (
                        <Spinner className='spinner' />
                    )}
                    {currentStep === VerificationStepEnum.SCANNING && (
                        <React.Fragment>
                            <Content>
                                <Header>
                                    <div className='header'>
                                        <Icon
                                            className='arrow-icon'
                                            icon={<ArrowTailIcon />}
                                            onClick={handleClose}
                                        />
                                        <Typography
                                            tag='div'
                                            variant='h2'
                                            className='title'
                                            // @ts-expect-error __html valid
                                            dangerouslySetInnerHTML={{ __html: title }}
                                        />
                                    </div>
                                </Header>

                                <div className='camera-mobile-actions'>
                                    {previewFile ? (
                                        <React.Fragment>
                                            <Button
                                                variant='secondary'
                                                className='secondary-button'
                                                onClick={handleRedoClick}
                                                size='xl'
                                            >
                                                {t('common.redoPhoto')}
                                            </Button>
                                            <Button
                                                variant='primary'
                                                onClick={handleFileChange}
                                                size='xl'
                                            >
                                                {t('common.continue')}
                                            </Button>
                                        </React.Fragment>
                                    ) : (
                                        <Button
                                            className='take-photo-button'
                                            variant='primary'
                                            onClick={capture}
                                        />
                                    )}
                                </div>
                            </Content>
                            {!!webcamVideoConstrains && !previewFile && (
                                <Webcam
                                    ref={webcamRef}
                                    audio={false}
                                    imageSmoothing={false}
                                    forceScreenshotSourceSize={true}
                                    videoConstraints={webcamVideoConstrains}
                                    screenshotFormat='image/jpeg'
                                    screenshotQuality={1}
                                    width={window.innerWidth * (android ? 2 : 1)}
                                    height={window.innerHeight * (android ? 2 : 1)}
                                />
                            )}
                            {previewFile?.preview && (
                                <img
                                    className='camera-preview'
                                    src={previewFile.preview as string}
                                    alt='preview'
                                />
                            )}
                            <CornerLine variant={variant} className='corner-line' />
                        </React.Fragment>
                    )}
                </React.Fragment>
            )}
        </Container>
    );
};
